import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { KeyboardEvent, PointerEvent as ReactPointerEvent, RefObject } from "react"
import { useUpdateModelNumbersOrder } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useObjectCategoryDrawerStore } from "store/configuration.store"
import { useMessage } from "hooks/message.hook"
import { isSameOrder, moveItemTo, resolveOrderedIds, resolveRowDropIndex } from "helpers/modelNumberOrder.helper"
import style from "./ModelNumbers.module.scss"

export const ORDER_SAVED_MESSAGE = "Order saved"
export const ORDER_SAVE_FAILED_MESSAGE = "Couldn't save the new order. The previous order is back."
// How long a reverted row keeps its "shake" class.
const REVERT_HIGHLIGHT_MS = 600

type TGhost = { key: string; x: number; y: number; offsetY: number; width: number }

// Priority reorder of the drawer's connected model numbers (#1487): drag a row by its grip, or pick
// it up from the keyboard (Space, arrows, Space / Esc). Each changed drop saves the full order.
export const useModelNumberReorder = (tableRef: RefObject<HTMLDivElement | null>) => {
	const { objectCategoryStore } = useObjectCategoryDrawerStore()
	const { mutate: saveOrder, isPending: isSaving } = useUpdateModelNumbersOrder()
	const { success, error } = useMessage()

	const savedOrder = objectCategoryStore.modelNumbers
	const [preview, setPreview] = useState<string[] | null>(null)
	const [dragKey, setDragKey] = useState<string | null>(null)
	const [pickedKey, setPickedKey] = useState<string | null>(null)
	const [ghost, setGhost] = useState<TGhost | null>(null)
	const [savingKey, setSavingKey] = useState<string | null>(null)
	const [revertKey, setRevertKey] = useState<string | null>(null)
	const [announcement, setAnnouncement] = useState("")
	const cleanupRef = useRef<(() => void) | null>(null)
	const revertTimer = useRef<ReturnType<typeof setTimeout>>()
	// A keyed row moving DOWN is re-inserted, which blurs its grip: that blur must not cancel the
	// pickup, and the grip gets focus back once the move has rendered. Set only by an arrow move.
	const isMovingRef = useRef(false)

	useEffect(
		() => () => {
			cleanupRef.current?.()
			clearTimeout(revertTimer.current)
		},
		[],
	)

	const displayed = preview ?? savedOrder

	useLayoutEffect(() => {
		if (!isMovingRef.current || !pickedKey) return
		isMovingRef.current = false
		const grip = tableRef.current?.querySelector<HTMLButtonElement>(
			`tr[data-row-key="${CSS.escape(pickedKey)}"] button[data-testid="model-number-reorder-grip"]`,
		)
		if (grip && document.activeElement !== grip) grip.focus()
	})

	const commit = (next: string[], movedKey: string) => {
		const { objectCategoryStore: current, modelNumberToId, setObjectCategory } = useObjectCategoryDrawerStore.getState()
		const previous = current.modelNumbers
		const categoryId = current.id
		if (isSameOrder(next, previous) || categoryId === null) return
		const ids = resolveOrderedIds(next, modelNumberToId)
		if (!ids) return

		setObjectCategory({ ...current, modelNumbers: next })
		// setObjectCategory clears the id map; put it back, the set of model numbers did not change.
		useObjectCategoryDrawerStore.getState().setModelNumberToId(modelNumberToId)
		setSavingKey(movedKey)
		saveOrder(
			{ categoryId, modelNumberIds: ids },
			{
				onSuccess: () => success(ORDER_SAVED_MESSAGE),
				onError: () => {
					error(ORDER_SAVE_FAILED_MESSAGE)
					const latest = useObjectCategoryDrawerStore.getState()
					// The drawer may show another object by now — never write this one's order into it.
					if (latest.objectCategoryStore.id !== categoryId) return
					const idMap = latest.modelNumberToId
					latest.setObjectCategory({ ...latest.objectCategoryStore, modelNumbers: previous })
					latest.setModelNumberToId(idMap)
					setRevertKey(movedKey)
					clearTimeout(revertTimer.current)
					revertTimer.current = setTimeout(() => setRevertKey(null), REVERT_HIGHLIGHT_MS)
				},
				onSettled: () => setSavingKey(null),
			},
		)
	}

	const rowMidpoints = (exclude: string) =>
		Array.from(tableRef.current?.querySelectorAll<HTMLElement>("tbody tr[data-row-key]") ?? [])
			.filter((row) => row.dataset.rowKey !== exclude)
			.map((row) => {
				const rect = row.getBoundingClientRect()
				return rect.top + rect.height / 2
			})

	const onGripPointerDown = (event: ReactPointerEvent<HTMLButtonElement>, key: string) => {
		if (event.button !== 0 || isSaving || pickedKey || dragKey) return
		event.preventDefault()
		const row = event.currentTarget.closest("tr")
		const rect = row?.getBoundingClientRect()
		if (!rect) return
		const offsetY = event.clientY - rect.top
		let order = savedOrder

		setDragKey(key)
		setPreview(order)
		setGhost({ key, x: rect.left, y: event.clientY, offsetY, width: rect.width })
		document.documentElement.classList.add(style.draggingRoot)

		const onMove = (e: PointerEvent) => {
			setGhost({ key, x: rect.left, y: e.clientY, offsetY, width: rect.width })
			const next = moveItemTo(order, key, resolveRowDropIndex(rowMidpoints(key), e.clientY))
			if (next !== order) {
				order = next
				setPreview(next)
			}
		}
		const finish = (shouldCommit: boolean) => {
			cleanupRef.current?.()
			if (shouldCommit) commit(order, key)
		}
		const onUp = () => finish(true)
		const onCancel = () => finish(false)
		const onKey = (e: globalThis.KeyboardEvent) => {
			if (e.key !== "Escape") return
			// Escape abandons the drag only; it must not also close the drawer.
			e.preventDefault()
			e.stopPropagation()
			finish(false)
		}

		cleanupRef.current = () => {
			window.removeEventListener("pointermove", onMove)
			window.removeEventListener("pointerup", onUp)
			window.removeEventListener("pointercancel", onCancel)
			window.removeEventListener("keydown", onKey, true)
			document.documentElement.classList.remove(style.draggingRoot)
			cleanupRef.current = null
			setDragKey(null)
			setPreview(null)
			setGhost(null)
		}
		window.addEventListener("pointermove", onMove)
		window.addEventListener("pointerup", onUp)
		window.addEventListener("pointercancel", onCancel)
		window.addEventListener("keydown", onKey, true)
	}

	const cancelPickup = () => {
		setPickedKey(null)
		setPreview(null)
	}

	const onGripKeyDown = (event: KeyboardEvent<HTMLButtonElement>, key: string) => {
		const isPicked = pickedKey === key
		if (event.key === " " || event.key === "Enter") {
			event.preventDefault()
			if (isSaving) return
			if (!isPicked) {
				setPickedKey(key)
				setPreview(savedOrder)
				setAnnouncement(`Picked up ${key}, position ${savedOrder.indexOf(key) + 1} of ${savedOrder.length}`)
				return
			}
			const next = preview ?? savedOrder
			cancelPickup()
			setAnnouncement(`Dropped ${key} at position ${next.indexOf(key) + 1}`)
			commit(next, key)
			return
		}
		if (!isPicked) return
		if (event.key === "ArrowUp" || event.key === "ArrowDown") {
			event.preventDefault()
			const current = preview ?? savedOrder
			const next = moveItemTo(current, key, current.indexOf(key) + (event.key === "ArrowUp" ? -1 : 1))
			if (next === current) return
			isMovingRef.current = true
			setPreview(next)
			setAnnouncement(`${key}, position ${next.indexOf(key) + 1} of ${next.length}`)
			return
		}
		if (event.key === "Escape") {
			// Keep the drawer open: Escape here only drops the picked-up row.
			event.preventDefault()
			event.stopPropagation()
			cancelPickup()
			setAnnouncement(`Reorder cancelled. ${key} stays at position ${savedOrder.indexOf(key) + 1}`)
		}
	}

	return {
		displayed,
		dragKey,
		pickedKey,
		ghost,
		isSaving,
		announcement,
		savingKey,
		revertKey,
		onGripPointerDown,
		onGripKeyDown,
		onGripBlur: (key: string) => {
			if (pickedKey === key && !isMovingRef.current) cancelPickup()
		},
	}
}
